(function () {
    "use strict";

    /**
     * Initialize the homepage hero slider.
     */
    function initializeHeroSlider(container) {
        const scope = container || document;
        const slider = scope.querySelector(".home-hero-swiper");

        if (!slider || typeof window.Swiper === "undefined") {
            return;
        }

        /*
         * Destroy an existing Swiper instance before creating it again.
         * This is useful inside the Shopify theme editor.
         */
        if (slider.swiper) {
            slider.swiper.destroy(true, true);
        }

        new window.Swiper(slider, {
            loop: true,
            speed: 900,
            effect: "fade",

            fadeEffect: {
                crossFade: true
            },

            autoplay: {
                delay: 5500,
                disableOnInteraction: false,
                pauseOnMouseEnter: true
            },

            pagination: {
                el: slider.querySelector(".home-hero-pagination"),
                clickable: true
            },

            keyboard: {
                enabled: true
            },

            a11y: {
                enabled: true
            }
        });
    }

    /**
     * Initialize homepage product wishlist buttons.
     *
     * Important:
     * This only changes the visual active state.
     * A real wishlist app can be connected later.
     */
    function initializeWishlistButtons(container) {
        const scope = container || document;
        const buttons = scope.querySelectorAll(".premium-wishlist-btn");

        buttons.forEach(function (button) {
            if (button.dataset.uphaarWishlistReady === "true") {
                return;
            }

            button.dataset.uphaarWishlistReady = "true";

            if (!button.hasAttribute("aria-pressed")) {
                button.setAttribute("aria-pressed", "false");
            }

            button.addEventListener("click", function (event) {
                event.preventDefault();
                event.stopPropagation();

                const isActive = button.classList.toggle("active");

                button.setAttribute(
                    "aria-pressed",
                    isActive ? "true" : "false"
                );
            });
        });
    }

    /**
     * Initialize homepage video and sound button.
     */
    function initializeHomepageVideo(container) {
        const scope = container || document;
        const videoSections = scope.querySelectorAll(
            ".homepage-video-section"
        );

        videoSections.forEach(function (videoSection) {
            const video = videoSection.querySelector(".homepage-video");
            const soundButton = videoSection.querySelector(
                ".homepage-video-sound"
            );

            if (!video) {
                return;
            }

            function startVideo() {
                const playPromise = video.play();

                if (
                    playPromise &&
                    typeof playPromise.catch === "function"
                ) {
                    playPromise.catch(function () {
                        video.muted = true;

                        const retryPromise = video.play();

                        if (
                            retryPromise &&
                            typeof retryPromise.catch === "function"
                        ) {
                            retryPromise.catch(function () {
                                // Autoplay may be blocked by the browser.
                            });
                        }
                    });
                }
            }

            video.muted = true;
            startVideo();

            if (
                !soundButton ||
                soundButton.dataset.uphaarSoundReady === "true"
            ) {
                return;
            }

            soundButton.dataset.uphaarSoundReady = "true";

            soundButton.addEventListener("click", function () {
                video.muted = !video.muted;

                const isUnmuted = !video.muted;

                soundButton.classList.toggle(
                    "is-unmuted",
                    isUnmuted
                );

                soundButton.setAttribute(
                    "aria-pressed",
                    isUnmuted ? "true" : "false"
                );

                soundButton.setAttribute(
                    "aria-label",
                    isUnmuted
                        ? "Mute video"
                        : "Turn video sound on"
                );

                startVideo();
            });
        });
    }

    /**
     * Initialize FAQ accordion.
     */
    function initializeFaq(container) {
        const scope = container || document;
        const faqLists = scope.querySelectorAll(".faq-list");

        faqLists.forEach(function (faqList) {
            const faqItems = faqList.querySelectorAll(".faq-item");

            function closeFaq(item) {
                const button = item.querySelector(".faq-question");
                const answer = item.querySelector(".faq-answer");

                if (!button || !answer) {
                    return;
                }

                item.classList.remove("active");
                button.setAttribute("aria-expanded", "false");
                answer.style.maxHeight = "0px";
            }

            function openFaq(item) {
                const button = item.querySelector(".faq-question");
                const answer = item.querySelector(".faq-answer");

                if (!button || !answer) {
                    return;
                }

                item.classList.add("active");
                button.setAttribute("aria-expanded", "true");
                answer.style.maxHeight = answer.scrollHeight + "px";
            }

            faqItems.forEach(function (item, index) {
                const button = item.querySelector(".faq-question");

                if (!button) {
                    return;
                }

                if (item.classList.contains("active") || index === 0) {
                    openFaq(item);
                } else {
                    closeFaq(item);
                }

                if (button.dataset.uphaarFaqReady === "true") {
                    return;
                }

                button.dataset.uphaarFaqReady = "true";

                button.addEventListener("click", function () {
                    const wasActive = item.classList.contains("active");

                    faqItems.forEach(function (faqItem) {
                        closeFaq(faqItem);
                    });

                    if (!wasActive) {
                        openFaq(item);
                    }
                });
            });
        });
    }

    /**
     * Initialize desktop and mobile header navigation.
     */
    function initializeHeader(container) {
        const scope = container || document;

        const header = scope.querySelector(".uphaar-header");

        if (!header) {
            return;
        }

        const menuToggle = header.querySelector(".uphaar-menu-toggle");
        const mobileMenu = header.querySelector(".uphaar-mobile-menu");

        const desktopDropdowns = header.querySelectorAll(
            ".uphaar-nav-dropdown"
        );

        const mobileDropdowns = header.querySelectorAll(
            ".uphaar-mobile-dropdown"
        );

        function closeDesktopDropdowns() {
            desktopDropdowns.forEach(function (dropdown) {
                dropdown.classList.remove("open");

                const trigger = dropdown.querySelector(
                    ".uphaar-dropdown-trigger"
                );

                if (trigger) {
                    trigger.setAttribute("aria-expanded", "false");
                }
            });
        }

        function closeMobileDropdowns() {
            mobileDropdowns.forEach(function (dropdown) {
                dropdown.classList.remove("open");

                const trigger = dropdown.querySelector(
                    ".uphaar-mobile-dropdown-trigger"
                );

                if (trigger) {
                    trigger.setAttribute("aria-expanded", "false");
                }
            });
        }

        function closeMobileMenu() {
            if (!menuToggle || !mobileMenu) {
                return;
            }

            mobileMenu.classList.remove("open");
            menuToggle.classList.remove("open");

            menuToggle.setAttribute("aria-expanded", "false");
            menuToggle.setAttribute("aria-label", "Open menu");

            document.body.classList.remove("uphaar-menu-open");

            closeMobileDropdowns();
        }

        if (
            menuToggle &&
            mobileMenu &&
            menuToggle.dataset.uphaarMenuReady !== "true"
        ) {
            menuToggle.dataset.uphaarMenuReady = "true";

            menuToggle.addEventListener("click", function () {
                const isOpen = mobileMenu.classList.toggle("open");

                menuToggle.classList.toggle("open", isOpen);

                menuToggle.setAttribute(
                    "aria-expanded",
                    isOpen ? "true" : "false"
                );

                menuToggle.setAttribute(
                    "aria-label",
                    isOpen ? "Close menu" : "Open menu"
                );

                document.body.classList.toggle(
                    "uphaar-menu-open",
                    isOpen
                );
            });
        }

        mobileDropdowns.forEach(function (dropdown) {
            const trigger = dropdown.querySelector(
                ".uphaar-mobile-dropdown-trigger"
            );

            if (
                !trigger ||
                trigger.dataset.uphaarDropdownReady === "true"
            ) {
                return;
            }

            trigger.dataset.uphaarDropdownReady = "true";

            trigger.addEventListener("click", function () {
                const wasOpen = dropdown.classList.contains("open");

                closeMobileDropdowns();

                if (!wasOpen) {
                    dropdown.classList.add("open");
                    trigger.setAttribute("aria-expanded", "true");
                }
            });
        });

        desktopDropdowns.forEach(function (dropdown) {
            const trigger = dropdown.querySelector(
                ".uphaar-dropdown-trigger"
            );

            if (
                !trigger ||
                trigger.dataset.uphaarDropdownReady === "true"
            ) {
                return;
            }

            trigger.dataset.uphaarDropdownReady = "true";

            trigger.addEventListener("click", function (event) {
                event.preventDefault();
                event.stopPropagation();

                const wasOpen = dropdown.classList.contains("open");

                closeDesktopDropdowns();

                if (!wasOpen) {
                    dropdown.classList.add("open");
                    trigger.setAttribute("aria-expanded", "true");
                }
            });
        });

        header
            .querySelectorAll(
                ".uphaar-mobile-nav-link[href], " +
                ".uphaar-mobile-submenu a"
            )
            .forEach(function (link) {
                if (link.dataset.uphaarCloseMenuReady === "true") {
                    return;
                }

                link.dataset.uphaarCloseMenuReady = "true";
                link.addEventListener("click", closeMobileMenu);
            });

        if (header.dataset.uphaarOutsideClickReady !== "true") {
            header.dataset.uphaarOutsideClickReady = "true";

            document.addEventListener("click", function (event) {
                if (
                    menuToggle &&
                    mobileMenu &&
                    !mobileMenu.contains(event.target) &&
                    !menuToggle.contains(event.target)
                ) {
                    closeMobileMenu();
                }

                desktopDropdowns.forEach(function (dropdown) {
                    if (!dropdown.contains(event.target)) {
                        dropdown.classList.remove("open");

                        const trigger = dropdown.querySelector(
                            ".uphaar-dropdown-trigger"
                        );

                        if (trigger) {
                            trigger.setAttribute(
                                "aria-expanded",
                                "false"
                            );
                        }
                    }
                });
            });

            document.addEventListener("keydown", function (event) {
                if (event.key !== "Escape") {
                    return;
                }

                closeMobileMenu();
                closeDesktopDropdowns();
            });

            window.addEventListener("resize", function () {
                if (window.innerWidth > 900) {
                    closeMobileMenu();
                }

                const activeFaqAnswers = document.querySelectorAll(
                    ".faq-item.active .faq-answer"
                );

                activeFaqAnswers.forEach(function (answer) {
                    answer.style.maxHeight =
                        answer.scrollHeight + "px";
                });
            });
        }
    }

    /**
     * Initialize all homepage functionality.
     */
    function initializeUphaarHomepage(container) {
        const scope = container || document;

        initializeHeroSlider(scope);
        initializeWishlistButtons(scope);
        initializeHomepageVideo(scope);
        initializeFaq(scope);
        initializeHeader(scope);
    }

    /**
     * Standard storefront page load.
     */
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", function () {
            initializeUphaarHomepage(document);
        });
    } else {
        initializeUphaarHomepage(document);
    }

    /**
     * Shopify theme editor support.
     */
    document.addEventListener(
        "shopify:section:load",
        function (event) {
            initializeUphaarHomepage(event.target);
        }
    );
})();